import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { isNativeApp, getNativeCurrentLocation, openExternalUrl } from 'common/webview';
import { useUserLocation } from 'hooks/useUserLocation';
import { LocationLoading } from 'components/common/LocationLoading';
import { IcPin, IcCheck } from 'components/common/icons/EmojiIcons';
import {
    AppScreen, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './ThemeRecommend.module.css';
import { fetchPlacePhotos } from 'common/utils/placePhotos';

const STORAGE_KEY = 'meeting_create_data';

interface RecommendPlace {
    id: string;
    name: string;
    address: string;
    lat: number;
    lng: number;
    imageUrl?: string;
    categoryLabel?: string;
    categoryIcon?: string;
    rating?: number;
}

export const ThemeRecommend = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { themeName, themeType, themeEmoji, themeIconKey, themeSubtitle, themeKeyword, meetingType } = location.state || {};

    const [places, setPlaces] = useState<RecommendPlace[]>([]);
    const [selectedPlace, setSelectedPlace] = useState<RecommendPlace | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [locationFailed, setLocationFailed] = useState(false); // 위치 획득 실패 상태
    const [reviews, setReviews] = useState<any[]>([]);
    const [isReviewLoading, setIsReviewLoading] = useState(false);
    const [showReviewId, setShowReviewId] = useState<string | null>(null);

    // 검색 타입 결정
    const searchType = (() => {
        switch (themeType) {
            case 'bar': return 'bar';
            case 'restaurant': return 'restaurant';
            case 'cafe': return 'cafe';
            case 'entertainment': return 'entertainment';
            case 'sports': return 'sports';
            default: return 'restaurant';
        }
    })();

    const { location: cachedLocation, fetchLocation: fetchUserLocation } = useUserLocation();
    // 캐시 위치가 없을 때만 로딩바 표시
    const isLocationLoading = !cachedLocation && !currentLocation && meetingType !== 'TRAVEL';

    // 현재 위치 (캐시 우선 → 없으면 요청) — 시청 fallback 제거
    useEffect(() => {
        if (meetingType === 'TRAVEL') {
            setIsLoading(false);
            return;
        }
        if (cachedLocation) {
            setCurrentLocation({ lat: cachedLocation.lat, lng: cachedLocation.lng });
            setLocationFailed(false);
            fetchUserLocation();
            return;
        }
        // 캐시 없으면 요청 — 실패 시 locationFailed 플래그로 UI 노출
        fetchUserLocation().then(loc => {
            if (loc) {
                setCurrentLocation({ lat: loc.lat, lng: loc.lng });
                setLocationFailed(false);
            } else {
                setLocationFailed(true);
                setIsLoading(false);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meetingType]);

    // 사용자 수동 재시도
    const handleRetryLocation = async () => {
        setLocationFailed(false);
        setIsLoading(true);
        const loc = await fetchUserLocation(true);
        if (loc) {
            setCurrentLocation({ lat: loc.lat, lng: loc.lng });
        } else {
            setLocationFailed(true);
            setIsLoading(false);
        }
    };

    // 추천 장소 검색 — 사용자 위치 기반 (TRAVEL 테마는 위치가 의미 없어 스킵)
    useEffect(() => {
        // 구멍 1 fix: TRAVEL은 여기까지 오면 안 됨 (사용자 위치 기반 추천이 의미 없음)
        if (meetingType === 'TRAVEL') {
            setIsLoading(false);
            return;
        }
        // DAY인데 currentLocation 없으면 아직 측위 대기
        if (!currentLocation) return;

        const loc = currentLocation;
        const fetchPlaces = async () => {
            setIsLoading(true);

            const callApi = async (params: any) => {
                try {
                    console.log('[ThemeRecommend] 검색 params:', params);
                    const res = await instance.get(getApiUrl('nearbyPlaces'), { params });
                    return res?.data?.data || [];
                } catch (e) {
                    // 구멍 3 fix: 에러 로깅 (조용히 먹지 않음)
                    console.error('[ThemeRecommend] nearbyPlaces 에러:', e);
                    return null; // null = API 에러, [] = 결과 없음
                }
            };

            // 1차: 키워드 + radius 5km
            const baseParams: any = { lat: loc.lat, lng: loc.lng, type: searchType, radius: 5000, limit: 10 };
            if (themeKeyword) baseParams.keyword = themeKeyword;
            let data = await callApi(baseParams);

            // 2차 fallback: 키워드가 있었으면 키워드 빼고 재시도 (구멍 2: 키워드 너무 구체적)
            if (data !== null && data.length === 0 && themeKeyword) {
                console.log('[ThemeRecommend] 키워드 제외하고 재시도');
                const { keyword, ...noKeyword } = baseParams;
                data = await callApi(noKeyword);
            }

            // 3차 fallback: radius 확장 (20km)
            if (data !== null && data.length === 0) {
                console.log('[ThemeRecommend] radius 20km로 재시도');
                const { keyword, ...wide } = baseParams;
                data = await callApi({ ...wide, radius: 20000 });
            }

            // API 자체 에러면 빈 배열로 간주
            if (data === null) data = [];

            const mapped: RecommendPlace[] = data.map((p: any) => ({
                id: p.placeId || p.name,
                name: p.name,
                address: p.address || '',
                lat: p.lat,
                lng: p.lng,
                imageUrl: p.photoUrl || undefined,
                categoryLabel: p.categoryLabel || searchType,
                rating: p.rating || undefined,
            }));
            setPlaces(mapped);

            // 사진 배치 요청 (실패해도 무시)
            const placeIds = mapped.slice(0, 6).map(p => p.id).filter(Boolean);
            if (placeIds.length > 0) {
                try {
                    const photos = await fetchPlacePhotos(placeIds);
                    if (Object.keys(photos).length > 0) {
                        setPlaces(prev => prev.map(p =>
                            photos[p.id] ? { ...p, imageUrl: photos[p.id].photoUrl || p.imageUrl, rating: photos[p.id].rating ?? p.rating } : p
                        ));
                    }
                } catch (e) {
                    console.warn('[ThemeRecommend] placePhotos 에러:', e);
                }
            }
            setIsLoading(false);
        };
        fetchPlaces();
    }, [currentLocation, searchType, meetingType, themeKeyword]);

    const handleSelectPlace = (place: RecommendPlace) => {
        setSelectedPlace(place);
        // 리뷰 토글 (같은 장소 클릭 시 닫기, 다른 장소 클릭 시 열기)
        if (showReviewId === place.id) {
            setShowReviewId(null);
        } else {
            setShowReviewId(place.id);
            fetchReviews(place);
        }
    };

    const fetchReviews = async (place: RecommendPlace) => {
        setIsReviewLoading(true);
        setReviews([]);
        try {
            const userLocale = navigator.language || 'ko';
            const res = await instance.get(getApiUrl('travelReviews'), {
                params: { name: place.name, address: place.address, lat: place.lat, lng: place.lng, limit: 5, locale: userLocale }
            });
            setReviews(res?.data?.data || []);
        } catch { setReviews([]); }
        finally { setIsReviewLoading(false); }
    };

    const handleConfirm = () => {
        const meetingData: any = {
            meetingName: themeName || '',
            description: themeSubtitle || '',
            meetingType: meetingType || 'DAY',
        };
        if (selectedPlace) {
            meetingData.selectedPlace = selectedPlace.name;
            meetingData.selectedPlaceInfo = {
                name: selectedPlace.name,
                address: selectedPlace.address,
                lat: selectedPlace.lat,
                lng: selectedPlace.lng,
            };
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meetingData));
        navigate('/meeting/create/step2');
    };

    const handleSkip = () => {
        const meetingData: any = {
            meetingName: themeName || '',
            description: themeSubtitle || '',
            meetingType: meetingType || 'DAY',
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meetingData));
        navigate('/meeting/create/step2');
    };

    // 여행 타입은 여행지 선택 화면으로 (장소 추천 대신)
    if (meetingType === 'TRAVEL') {
        const meetingData = { meetingName: themeName || '', description: themeSubtitle || '', meetingType: 'TRAVEL' };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meetingData));
        navigate('/meeting/create/travel-step3', { replace: true });
        return null;
    }

    return (
        <AppScreen title={t('themeRecommend.headerTitle')} bar="sub" hasFooter>
            <LocationLoading visible={isLocationLoading} />

            <PageIntro
                title={themeName || t('themeRecommend.defaultName')}
                desc={themeSubtitle || t('themeRecommend.defaultSub')}
            />

            <Section spacing="tight" last>
                <SectionHeader
                    title={t('themeRecommend.sectionTitle')}
                    count={places.length || undefined}
                />

                {locationFailed ? (
                    <div className={styles.empty}>
                        <span className={styles.emptyTitle}>{t('themeRecommend.locationFailTitle')}</span>
                        <span className={styles.emptyDesc}>{t('themeRecommend.locationFailDesc')}</span>
                        <div className={styles.emptyAction}>
                            <Button variant="secondary" size="lg" block onClick={handleRetryLocation}>
                                {t('common.retry')}
                            </Button>
                        </div>
                    </div>
                ) : isLoading ? (
                    <p className={styles.state}>{t('themeRecommend.searching')}</p>
                ) : places.length === 0 ? (
                    <p className={styles.state}>{t('themeRecommend.noPlaces')}</p>
                ) : (
                    <ul className={styles.list}>
                        {places.map(place => {
                            const on = selectedPlace?.id === place.id;
                            return (
                                <li key={place.id}>
                                    <button
                                        type="button"
                                        className={on ? `${styles.row} ${styles.rowOn}` : styles.row}
                                        onClick={() => handleSelectPlace(place)}
                                        aria-pressed={on}
                                    >
                                        <span className={styles.thumb}>
                                            {place.imageUrl
                                                ? <img src={place.imageUrl} alt="" loading="lazy" />
                                                : <IcPin size={20} />}
                                        </span>
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowTitle}>{place.name}</span>
                                            <span className={styles.rowMeta}>{place.address}</span>
                                            <span className={styles.rowTags}>
                                                {place.rating ? (
                                                    <span className={styles.tag}>{place.rating}</span>
                                                ) : null}
                                                {place.categoryLabel ? (
                                                    <span className={styles.tag}>{place.categoryLabel}</span>
                                                ) : null}
                                            </span>
                                        </span>
                                        <span className={styles.check} aria-hidden>
                                            {on && <IcCheck size={14} />}
                                        </span>
                                    </button>

                                    {showReviewId === place.id && (
                                        <div className={styles.reviews}>
                                            {isReviewLoading ? (
                                                <p className={styles.state}>{t('themeRecommend.reviewsLoading')}</p>
                                            ) : reviews.length === 0 ? (
                                                <p className={styles.state}>{t('themeRecommend.reviewsEmpty')}</p>
                                            ) : (
                                                reviews.map((review, idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        className={styles.review}
                                                        onClick={() => review.link && openExternalUrl(review.link)}
                                                    >
                                                        {review.thumbnail && (
                                                            <span className={styles.reviewThumb}>
                                                                <img src={review.thumbnail} alt="" loading="lazy" />
                                                            </span>
                                                        )}
                                                        <span className={styles.reviewBody}>
                                                            <span className={styles.reviewTitle}>{review.title}</span>
                                                            <span className={styles.reviewDesc}>{review.description}</span>
                                                            <span className={styles.reviewMeta}>
                                                                {[review.bloggerName, review.postDate].filter(Boolean).join(' · ')}
                                                            </span>
                                                        </span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}

                <div className={styles.skipAction}>
                    <Button variant="ghost" size="lg" block onClick={handleSkip}>
                        {t('themeRecommend.skipLater')}
                    </Button>
                </div>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleConfirm}
                    disabled={!selectedPlace}
                >
                    {selectedPlace
                        ? t('themeRecommend.createWithPlace', { name: selectedPlace.name })
                        : t('themeRecommend.selectPlace')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
